import { createContext, useContext, useState, useCallback, useRef } from "react";
import Notification from "./Notification";

const NotificationContext = createContext(null);

let idCounter = 0;

// Janela em que uma notificação idêntica (mesmo tipo/título/mensagem) é ignorada.
// Evita duplicatas causadas pelo StrictMode (que dispara efeitos 2x em dev) ou
// pelo mesmo erro chegando por caminhos diferentes.
const DEDUPE_WINDOW_MS = 800;

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const lastShownRef = useRef(new Map());

  const remove = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const close = useCallback((id) => {
    // trigger closing animation, then remove
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, closing: true } : n)));
    setTimeout(() => remove(id), 320);
  }, [remove]);

  const show = useCallback((type, title, message) => {
    const key = `${type}|${title}|${message}`;
    const now = Date.now();
    const last = lastShownRef.current.get(key);
    if (last && now - last < DEDUPE_WINDOW_MS) {
      // Duplicata recente: ignora para não empilhar a mesma notificação 2x
      return;
    }
    lastShownRef.current.set(key, now);

    const id = ++idCounter;

    setNotifications((prev) => [
      ...prev,
      {
        id,
        type,
        title,
        message,
        closing: false,
      },
    ]);

    // auto close after 5s
    setTimeout(() => {
      close(id);
    }, 5000);
  }, [close]);

  const api = {
    showError: (msg, title = "") => show("error", title, msg),
    showSuccess: (msg, title = "") => show("success", title, msg),
    showWarning: (msg, title = "") => show("warning", title, msg),
    showInfo: (msg, title = "") => show("info", title, msg),
  };

  return (
    <NotificationContext.Provider value={api}>
      {children}

      {/* Container das notificações */}
      <div className="notification-stack">
        {notifications.map((n) => (
          <Notification
            key={n.id}
            {...n}
            onClose={() => close(n.id)}
          />
        ))}
      </div>
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error("useNotification must be used inside NotificationProvider");
  }
  return ctx;
}
